import ApiService from './api';
import { safeArray } from '../utils/money';

// 团队成员状态
export type TeamMemberStatus = 'active' | 'invited' | 'suspended';

// 团队成员
export interface TeamMember {
  id: string;
  user_id: string;
  email: string;
  first_name: string;
  last_name: string;
  role: string;
  permissions: string[];
  status: TeamMemberStatus;
  active: boolean;
  invited_at?: string;
  accepted_at?: string;
  last_active?: string;
  created_at: string;
}

// 角色定义（含默认权限）
export interface RoleDef {
  role: string;
  permissions: string[];
}

// 角色与权限元数据
export interface RolesMeta {
  roles: RoleDef[];
  permissions: string[]; // 全部 16 个 scope
}

// 邀请/添加成员入参
export interface InviteMemberInput {
  email: string;
  first_name: string;
  last_name: string;
  role: string;
  permissions?: string[];
}

// 更新成员入参
export interface UpdateMemberInput {
  first_name?: string;
  last_name?: string;
  role?: string;
  permissions?: string[];
  active: boolean;
}

export class TeamService {
  /**
   * 获取团队成员列表
   */
  static async listMembers(): Promise<TeamMember[]> {
    const response = await ApiService.get<TeamMember[]>('/merchant/settings/team');
    return safeArray<TeamMember>(response.data).map((m) => ({
      ...m,
      permissions: safeArray<string>(m.permissions),
    }));
  }

  /**
   * 获取角色与全部权限范围
   */
  static async getRoles(): Promise<RolesMeta> {
    const response = await ApiService.get<RolesMeta>('/merchant/settings/team/roles');
    const data = response.data || ({ roles: [], permissions: [] } as RolesMeta);
    return {
      roles: safeArray<RoleDef>(data.roles).map((r) => ({
        role: r.role,
        permissions: safeArray<string>(r.permissions),
      })),
      permissions: safeArray<string>(data.permissions),
    };
  }

  /**
   * 邀请/添加成员
   */
  static async inviteMember(input: InviteMemberInput): Promise<TeamMember> {
    const response = await ApiService.post<TeamMember>('/merchant/settings/team', input);
    return response.data;
  }

  /**
   * 更新成员（角色/权限/启用停用）
   */
  static async updateMember(id: string, input: UpdateMemberInput): Promise<TeamMember> {
    const response = await ApiService.put<TeamMember>(`/merchant/settings/team/${id}`, input);
    return response.data;
  }

  /**
   * 移除成员
   */
  static async removeMember(id: string): Promise<void> {
    await ApiService.delete(`/merchant/settings/team/${id}`);
  }
}

export default TeamService;
